import React, { useState, useEffect } from 'react'
import { DataSource, Folder } from '../types'
import { FileIcon } from './common/FileIcon'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import { SelectionControls, SelectionCheckbox } from './management'
import { RiDeleteBin2Fill } from "react-icons/ri";
import { useTranslate } from '../hooks/useTranslate';

const THEME = {
    TEXT: {
        onLight: 'text-black',
        onLightSecondary: 'text-black'
    }
};

interface FolderViewProps {
    selectedFolder: Folder
    dataObjects: DataSource[]
    onDeleteDataObject: (id: number) => void
    onRemoveFromFolder: (id: number) => void
    onDataObjectClick: (dataObject: DataSource) => void
    onRefreshDataObject?: (dataSource: DataSource) => Promise<void>
    // Multi-select props
    isSelecting?: boolean
    selectedIds?: number[]
    onCardSelection?: (id: number, checked: boolean) => void
    // Management props
    onToggleSelect?: () => void
    hasSelectableItems?: boolean
    selectedCount?: number
    onBulkMove?: () => void
    onBulkDelete?: () => void
    onSelectAll?: () => void
    onClearSelection?: () => void
}

const FolderView: React.FC<FolderViewProps> = ({
    selectedFolder,
    dataObjects,
    onDeleteDataObject,
    onRemoveFromFolder,
    onDataObjectClick,
    onRefreshDataObject,
    isSelecting = false,
    selectedIds = [],
    onCardSelection,
    onToggleSelect,
    hasSelectableItems = false,
    selectedCount = 0,
    onBulkMove,
    onBulkDelete,
    onSelectAll,
    onClearSelection
}) => {
    const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
    const [isAnimating, setIsAnimating] = useState(true);
    const [justSelected, setJustSelected] = useState<number | null>(null);
    const { t } = useTranslate();

    // Trigger animation on mount
    useEffect(() => {
        setIsAnimating(true);
        const timer = setTimeout(() => setIsAnimating(false), 100);
        return () => clearTimeout(timer);
    }, [selectedFolder.id]); // Re-animate when folder changes

    const sortedDataObjects = dataObjects;

    const handleDeleteClick = (e: React.MouseEvent, id: number, name: string) => {
        // Prevent parent card onClick from firing (which would open the actions modal)
        e.stopPropagation();
        setDeleteTarget({ id, name });
    };

    const handleCardClick = (dataSource: DataSource, event: React.MouseEvent) => {
        // Check if click originated from checkbox area
        if ((event.target as HTMLInputElement).type === 'checkbox') {
            return; // Don't handle card click if checkbox was clicked
        }

        // Prevent card click if this card was just selected via hover checkbox
        if (justSelected === dataSource.id) {
            setJustSelected(null); // Clear the flag
            return;
        }

        if (isSelecting && onCardSelection) {
            onCardSelection(dataSource.id, !selectedIds.includes(dataSource.id));
        } else if (!isSelecting && selectedIds.length === 0) {
            // Only allow card click if no items are selected and not in selection mode
            onDataObjectClick(dataSource);
        }
    };

    return (
        <div className="space-y-6">
            <div className="folder-container">
                <div className="mb-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-black">
                            {t('Data Objects')} - <span className="notranslate">{selectedFolder.name}</span>
                        </h2>
                    </div>
                </div>

                {/* Selection Controls */}
                {isSelecting && (
                    <SelectionControls
                        selectedCount={selectedCount}
                        totalCount={dataObjects.length}
                        onBulkMove={selectedCount > 0 ? onBulkMove : undefined}
                        onBulkDelete={selectedCount > 0 ? onBulkDelete : undefined}
                        onSelectAll={onSelectAll}
                        onClearSelection={onClearSelection}
                    />
                )}
                {dataObjects.length === 0 ? (
                    <div className="text-center py-12">
                        <svg className="w-12 h-12 mx-auto text-black mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="text-lg font-medium text-black mb-2">{t('No data objects in this folder')}</h3>
                        <p className="text-black mb-4">{t('Get started by adding data objects to this folder.')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                        {sortedDataObjects.map((dataSource, index) => {
                            const isSelected = selectedIds.includes(dataSource.id);
                            return (
                                <div
                                    key={dataSource.id}
                                    className={`data-card rounded-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:scale-105 hover:-translate-y-1 shadow-xl hover:shadow-red-500/20 relative overflow-hidden ${isSelected ? 'ring-2 ring-red-500 ring-offset-2' : ''}`}
                                    style={{
                                        background: '#ffffff',
                                        color: '#000000',
                                        animationDelay: `${index * 150}ms`,
                                        border: '2px solid #d1d5db',
                                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.3s ease-out, box-shadow 0.3s ease-out',
                                        minHeight: '79px'
                                    }}
                                    onClick={(event) => handleCardClick(dataSource, event)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.boxShadow = '0 20px 40px -10px rgba(211, 17, 69, 0.4), 0 10px 25px -5px rgba(211, 17, 69, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
                                    }}
                                >
                                    {/* Hover Selection Button - appears on hover when not in selecting mode */}
                                    {!isSelecting && onCardSelection && (
                                        <div className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                            <div className="mr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        onCardSelection(dataSource.id, e.target.checked);
                                                        // Mark this card as just selected to prevent card click
                                                        if (e.target.checked) {
                                                            setJustSelected(dataSource.id);
                                                            // Clear the flag after a short delay
                                                            setTimeout(() => setJustSelected(null), 100);
                                                        }
                                                        // Trigger selection mode if not already active and we're selecting an item
                                                        if (!isSelecting && e.target.checked && onToggleSelect) {
                                                            onToggleSelect();
                                                        }
                                                    }}
                                                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                                                    title="Select this item"
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {/* Selection Mode Checkbox - positioned absolutely at bottom-left */}
                                    {isSelecting && (
                                        <SelectionCheckbox
                                            id={dataSource.id}
                                            isSelected={isSelected}
                                            onSelectionChange={(id, checked) => {
                                                onCardSelection?.(id, checked);
                                            }}
                                        />
                                    )}
                                    <div className="p-2">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center space-x-3 flex-1 min-w-0 pr-2">
                                                <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                                                    <FileIcon
                                                        fileName={dataSource.name}
                                                        fileType={dataSource.type}
                                                        className="w-8 h-8 sm:w-12 sm:h-12"
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className={`text-xs sm:text-sm font-semibold truncate notranslate ${THEME.TEXT.onLight}`}>{dataSource.name}</h3>
                                                    <p className={`text-xs font-medium notranslate ${THEME.TEXT.onLightSecondary}`}>
                                                        {dataSource.type === 'databricks' ? 'Databricks' : dataSource.type.toUpperCase()}
                                                    </p>
                                                </div>
                                            </div>
                                            {!isSelecting && (
                                                <div className="flex items-center space-x-1 flex-shrink-0">
                                                    <button
                                                        onClick={(e) => handleDeleteClick(e, dataSource.id, dataSource.name)}
                                                        className="p-1 hover:bg-red-500 hover:text-white rounded transition-all duration-200 flex-shrink-0"
                                                        style={{ color: '#000000' }}
                                                        title="Delete data object"
                                                    >
                                                        <RiDeleteBin2Fill className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-end mt-0.5">
                                            <div className="flex items-center space-x-1">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${dataSource.status === 'processed'
                                                    ? 'bg-green-100 text-green-800'
                                                    : dataSource.status === 'processing'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-red-100 text-red-800'
                                                    }`}>
                                                    {dataSource.status === 'processing' && (
                                                        <div className="loader-red loader-sm"></div>
                                                    )}
                                                    {dataSource.status === 'processed'
                                                        ? t('Analyzed')
                                                        : dataSource.status === 'processing'
                                                            ? t('Processing')
                                                            : dataSource.status === 'analyzing'
                                                                ? ''
                                                                : dataSource.status === 'error'
                                                                    ? t('Error')
                                                                    : t('Unknown')}
                                                </span>
                                                {/* Refresh button for processing/analyzing status */}
                                                {(dataSource.status === 'processing' || dataSource.status === 'analyzing') && onRefreshDataObject && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onRefreshDataObject(dataSource);
                                                        }}
                                                        className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
                                                        title="Refresh status"
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modals */}
            {/* AddDataSourceModal removed - handled by parent DataSource component */}

            <DeleteConfirmationModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => {
                    if (deleteTarget) {
                        onDeleteDataObject(deleteTarget.id);
                        setDeleteTarget(null);
                    }
                }}
                secondaryLabel={t('Remove from Folder')}
                onSecondary={() => {
                    if (deleteTarget) {
                        onRemoveFromFolder(deleteTarget.id);
                        setDeleteTarget(null);
                    }
                }}
                entityName={deleteTarget?.name || ''}
                title={t('Delete Data Object')}
                confirmLabel={t('Delete Permanently')}
            />
        </div>
    )
}

export default FolderView